/**
 * Os planos do RPG Desk (docs/spec/planos.md): o Grátis e três pagos, cada um com mais direitos. A
 * grade fica toda aqui, e o servidor e o cliente usam a mesma. O servidor confere cada direito no ponto
 * da ação; a tela só mostra e avisa antes.
 *
 * Os planos são aninhados: cada um tem tudo o que o anterior tem, com valores iguais ou maiores (o
 * teste confere). Um direito novo entra em `EntitlementsSchema`, em `ENTITLEMENT_INFO` e em cada plano
 * de `PLANS`, e o TypeScript cobra os três.
 */
import { z } from 'zod';

const MB = 1024 * 1024;
const GB = 1024 * MB;
const DAY = 24 * 60 * 60 * 1000;

/** Os planos, do menor ao maior: a posição é o nível. Os ids nunca aparecem na tela (o nome vem do i18n). */
export const PLAN_IDS = ['free', 'tier1', 'tier2', 'tier3'] as const;
export const PlanIdSchema = z.enum(PLAN_IDS);
export type PlanId = z.infer<typeof PlanIdSchema>;
/** O plano de quem não tem plano gravado. */
export const DEFAULT_PLAN: PlanId = 'free';

/** O que um plano dá: um campo por direito. */
export const EntitlementsSchema = z.object({
  /** espaço do Desk Cloud (bytes) */
  storageBytes: z.number().int().positive(),
  /** maior arquivo de um envio (bytes); o servidor ainda corta no teto dele */
  maxFileBytes: z.number().int().positive(),
});
export type Entitlements = z.infer<typeof EntitlementsSchema>;
export type EntitlementKey = keyof Entitlements;

/**
 * Como um direito se compara e aparece na tela:
 * - `bytes`: um número positivo, nunca ilimitado (espaço custa);
 * - `count`: um inteiro ≥ 0, ou `null` para "sem limite" (o maior de todos);
 * - `flag`: sim ou não.
 * Hoje a grade só usa `bytes`; os outros ficam prontos para o primeiro direito que precisar deles.
 */
export type EntitlementKind = 'bytes' | 'count' | 'flag';

/**
 * Uma linha por direito: o TypeScript cobra quando entra um campo novo. `min` e `max`: a faixa que o
 * admin pode dar ao editar um plano (docs/spec/planos.md, "Editar os planos").
 */
export const ENTITLEMENT_INFO: { readonly [K in EntitlementKey]: { kind: EntitlementKind; min?: number; max?: number } } = {
  storageBytes: { kind: 'bytes', min: MB, max: 10 * 1024 * GB },
  maxFileBytes: { kind: 'bytes', min: MB, max: 10 * GB },
};

/** A grade: os direitos de cada plano. Os testes do servidor passam uma pequena. */
export type PlanGrid = { readonly [P in PlanId]: Entitlements };

export const PLANS: PlanGrid = {
  free: { storageBytes: 100 * MB, maxFileBytes: 25 * MB },
  tier1: { storageBytes: 5 * GB, maxFileBytes: 100 * MB },
  tier2: { storageBytes: 25 * GB, maxFileBytes: 100 * MB },
  tier3: { storageBytes: 100 * GB, maxFileBytes: 100 * MB },
};

export const PLAN_LIMITS = {
  /** o prazo mais curto de um plano, a partir de agora */
  minDurationMs: 60 * 1000,
  /** o prazo mais longo de um plano, a partir de agora */
  maxDurationMs: 5 * 365 * DAY,
  /** a nota interna do admin */
  note: 500,
} as const;

/**
 * O que importa da linha de `user_plans` para decidir o plano. `planId` é texto: pode ser um plano que
 * já saiu da grade.
 */
export type PlanRecord = { planId: string; expiresAt: number | null };

export function isPlanId(id: string): id is PlanId {
  return (PLAN_IDS as readonly string[]).includes(id);
}

/** O plano vale agora? Sem prazo, sempre; com prazo, até o instante dele (nesse instante já venceu). */
export function planActive(expiresAt: number | null, now: number): boolean {
  return expiresAt === null || expiresAt > now;
}

/**
 * O plano que vale para a conta, nesta ordem: sem linha, o Grátis; um plano que o código não conhece, o
 * Grátis; vencido, o Grátis; senão, o gravado. Na dúvida, nunca um plano maior.
 */
export function effectivePlan(record: PlanRecord | null | undefined, now: number): PlanId {
  if (!record || !isPlanId(record.planId) || !planActive(record.expiresAt, now)) return DEFAULT_PLAN;
  return record.planId;
}

/** O que o servidor consegue oferecer, por direito numérico: corta o que o plano promete. */
export type PlanCeilings = Partial<Record<EntitlementKey, number>>;

/** Os direitos do plano, cortados nos tetos do servidor. */
export function entitlementsFor(plan: PlanId, ceilings: PlanCeilings = {}, grid: PlanGrid = PLANS): Entitlements {
  const out: Record<string, unknown> = { ...grid[plan] };
  for (const key of Object.keys(ENTITLEMENT_INFO) as EntitlementKey[]) {
    const ceiling = ceilings[key];
    const value = out[key];
    if (ceiling === undefined) continue;
    // `null` (sem limite) também é cortado: o teto do servidor vale para todos
    if (value === null || typeof value === 'number') out[key] = value === null ? ceiling : Math.min(value, ceiling);
  }
  return out as Entitlements;
}

/**
 * `a` é pelo menos `b`, para o tipo do direito? (`null` é o maior `count`; um `flag` ligado ganha do
 * desligado.) É a régua do aninhamento dos planos.
 */
export function entitlementAtLeast(kind: EntitlementKind, a: unknown, b: unknown): boolean {
  if (kind === 'flag') return a === true || b !== true;
  if (kind === 'count' && a === null) return true;
  if (kind === 'count' && b === null) return false;
  return typeof a === 'number' && typeof b === 'number' && a >= b;
}

/** O direito existe no plano? (`bytes`: maior que zero; `count`: sem limite ou maior que zero; `flag`: ligado.) */
function entitlementGiven(kind: EntitlementKind, value: unknown): boolean {
  if (kind === 'flag') return value === true;
  if (kind === 'count' && value === null) return true;
  return typeof value === 'number' && value > 0;
}

/** O primeiro plano em que o direito existe, para a dica "A partir do Plano 2"; null se nenhum dá. */
export function firstPlanWith(key: EntitlementKey, grid: PlanGrid = PLANS): PlanId | null {
  return PLAN_IDS.find((id) => entitlementGiven(ENTITLEMENT_INFO[key].kind, grid[id][key])) ?? null;
}

/** O que a pessoa recebe do próprio plano: o que vale agora, já com os tetos do servidor. */
export const PlanViewSchema = z.object({
  id: PlanIdSchema,
  /** o fim do plano pago; null sem prazo (e sempre no Grátis) */
  expiresAt: z.number().int().nonnegative().nullable(),
  entitlements: EntitlementsSchema,
});
export type PlanView = z.infer<typeof PlanViewSchema>;

/** O `PlanView` de uma conta, a partir da linha dela (ou de nenhuma). */
export function planView(record: PlanRecord | null | undefined, now: number, ceilings: PlanCeilings = {}, grid: PlanGrid = PLANS): PlanView {
  const id = effectivePlan(record, now);
  return { id, expiresAt: id === DEFAULT_PLAN ? null : (record?.expiresAt ?? null), entitlements: entitlementsFor(id, ceilings, grid) };
}

/** Por que um prazo não vale para um plano: no passado (ou cedo demais), longe demais, ou o Grátis com prazo. */
export type PlanExpiryProblem = 'past' | 'too-far' | 'free';

export function planExpiryProblem(plan: PlanId, expiresAt: number | null, now: number): PlanExpiryProblem | null {
  if (plan === DEFAULT_PLAN) return expiresAt === null ? null : 'free';
  if (expiresAt === null) return null;
  if (!Number.isSafeInteger(expiresAt) || expiresAt < now + PLAN_LIMITS.minDurationMs) return 'past';
  if (expiresAt > now + PLAN_LIMITS.maxDurationMs) return 'too-far';
  return null;
}

/* ------------------------------------------------------------------ editar os planos (docs/spec/planos.md) */

/**
 * O código guarda os padrões (`PLANS` e os nomes do i18n); o banco guarda o que um admin da
 * configuração do servidor mudou em cada plano (`plan_configs`). O catálogo é a mescla dos dois.
 */
export const PLAN_CONFIG_LIMITS = {
  /** o nome editado, em cada língua */
  name: 30,
  /** o preço por mês, em centavos (R$ 10.000) */
  maxPriceCents: 1_000_000,
} as const;

/** O nome que o admin deu ao plano, por língua; sem ele, vale o do i18n. */
export const PlanNamesSchema = z.object({
  pt: z.string().trim().max(PLAN_CONFIG_LIMITS.name).optional(),
  en: z.string().trim().max(PLAN_CONFIG_LIMITS.name).optional(),
});
export type PlanNames = z.infer<typeof PlanNamesSchema>;

/** O que vale de um plano: os direitos, o nome editado e o preço por mês (centavos; null sem preço). */
export const PlanConfigSchema = z.object({
  entitlements: EntitlementsSchema,
  names: PlanNamesSchema,
  priceCents: z.number().int().min(0).max(PLAN_CONFIG_LIMITS.maxPriceCents).nullable(),
});
export type PlanConfig = z.infer<typeof PlanConfigSchema>;
export type PlanCatalog = { readonly [P in PlanId]: PlanConfig };

/** O que a pessoa recebe do catálogo: os direitos e os nomes de cada plano (o preço fica com o admin). */
export const PublicPlanSchema = z.object({ entitlements: EntitlementsSchema, names: PlanNamesSchema });
export type PublicPlan = z.infer<typeof PublicPlanSchema>;
export const PublicPlanCatalogSchema = z.record(PlanIdSchema, PublicPlanSchema);
export type PublicPlanCatalog = { readonly [P in PlanId]: PublicPlan };

/** Uma linha de `plan_configs` como o banco guarda: os direitos e os nomes em JSON, como texto. */
export type PlanConfigRow = { planId: string; entitlements: string; names: string; priceCents: number | null };

/** O catálogo sem nada editado: a grade do código, sem nomes editados e sem preço. */
export function defaultCatalog(grid: PlanGrid = PLANS): PlanCatalog {
  return Object.fromEntries(PLAN_IDS.map((id) => [id, { entitlements: { ...grid[id] }, names: {}, priceCents: null }])) as PlanCatalog;
}

/** A grade (só os direitos) de um catálogo. */
export function gridOf(catalog: PlanCatalog | PublicPlanCatalog): PlanGrid {
  return Object.fromEntries(PLAN_IDS.map((id) => [id, catalog[id].entitlements])) as PlanGrid;
}

/** O catálogo para a pessoa: sem o preço. */
export function publicCatalog(catalog: PlanCatalog): PublicPlanCatalog {
  return Object.fromEntries(PLAN_IDS.map((id) => [id, { entitlements: catalog[id].entitlements, names: catalog[id].names }])) as PublicPlanCatalog;
}

/** Por que um valor não vale para um direito: fora da faixa que o admin pode dar. */
export function entitlementProblem(key: EntitlementKey, value: unknown): 'range' | null {
  const { min, max } = ENTITLEMENT_INFO[key];
  if (typeof value !== 'number') return null;
  if ((min !== undefined && value < min) || (max !== undefined && value > max)) return 'range';
  return null;
}

/** O primeiro direito que quebra o aninhamento: um plano com menos que o anterior (`below`). */
export type NestingProblem = { key: EntitlementKey; plan: PlanId; below: PlanId };

export function nestingProblem(grid: PlanGrid): NestingProblem | null {
  for (const key of Object.keys(ENTITLEMENT_INFO) as EntitlementKey[]) {
    for (let i = 1; i < PLAN_IDS.length; i += 1) {
      const plan = PLAN_IDS[i]!;
      const below = PLAN_IDS[i - 1]!;
      if (!entitlementAtLeast(ENTITLEMENT_INFO[key].kind, grid[plan][key], grid[below][key])) return { key, plan, below };
    }
  }
  return null;
}

/** Os direitos editados de uma linha: só as chaves que o código conhece, cada uma dentro da faixa. */
const StoredEntitlementsSchema = EntitlementsSchema.partial();

/**
 * Mescla as linhas do banco sobre os padrões do código. Uma linha estragada (JSON inválido, valor fora
 * da faixa, plano desconhecido) fica de fora; se a grade mesclada quebrar o aninhamento, valem os
 * direitos do código. Os problemas voltam em texto, para o aviso no log. Na dúvida, o código.
 */
export function catalogFrom(rows: readonly PlanConfigRow[], defaults: PlanGrid = PLANS): { catalog: PlanCatalog; problems: string[] } {
  const problems: string[] = [];
  const catalog = defaultCatalog(defaults) as { [P in PlanId]: PlanConfig };
  for (const row of rows) {
    if (!isPlanId(row.planId)) {
      problems.push(`plano desconhecido em plan_configs: ${row.planId}`);
      continue;
    }
    const entitlements = StoredEntitlementsSchema.safeParse(parseJson(row.entitlements));
    const names = PlanNamesSchema.safeParse(parseJson(row.names));
    const price = row.priceCents;
    const priceOk = price === null || (Number.isSafeInteger(price) && price >= 0 && price <= PLAN_CONFIG_LIMITS.maxPriceCents);
    const outOfRange = entitlements.success && (Object.keys(entitlements.data) as EntitlementKey[]).some((key) => entitlementProblem(key, entitlements.data[key]));
    if (!entitlements.success || !names.success || !priceOk || outOfRange) {
      problems.push(`linha inválida em plan_configs: ${row.planId}`);
      continue;
    }
    catalog[row.planId] = {
      entitlements: { ...catalog[row.planId].entitlements, ...entitlements.data },
      names: normalizeNames(names.data),
      priceCents: row.planId === DEFAULT_PLAN ? null : price,
    };
  }
  const nesting = nestingProblem(gridOf(catalog));
  if (nesting) {
    problems.push(`os planos editados quebram o aninhamento (${nesting.key}: ${nesting.plan} < ${nesting.below}); valem os direitos do código`);
    for (const id of PLAN_IDS) catalog[id] = { ...catalog[id], entitlements: { ...defaults[id] } };
  }
  return { catalog, problems };
}

/** Os nomes sem os vazios, sempre na mesma ordem (pt, en): vazio é "use o do i18n". */
export function normalizeNames(names: PlanNames): PlanNames {
  const pt = names.pt?.trim();
  const en = names.en?.trim();
  return { ...(pt ? { pt } : {}), ...(en ? { en } : {}) };
}

/** Os dois planos dão o mesmo (direitos, nomes e preço)? */
export function samePlanConfig(a: PlanConfig, b: PlanConfig): boolean {
  const names = (n: PlanNames) => JSON.stringify(normalizeNames(n));
  return (
    (Object.keys(ENTITLEMENT_INFO) as EntitlementKey[]).every((key) => a.entitlements[key] === b.entitlements[key]) &&
    names(a.names) === names(b.names) &&
    a.priceCents === b.priceCents
  );
}

/** Os direitos que diferem do padrão: é o que a linha do banco guarda. */
export function entitlementsDiff(entitlements: Entitlements, defaults: Entitlements): Partial<Entitlements> {
  return Object.fromEntries((Object.keys(ENTITLEMENT_INFO) as EntitlementKey[]).filter((key) => entitlements[key] !== defaults[key]).map((key) => [key, entitlements[key]]));
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}
